import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import type { MessagesResponse, ConversationsResponse, ChatMessage } from '../types/resource';

export const MESSAGES_PAGE_SIZE = 20;
export const CONVERSATIONS_PAGE_SIZE = 20;

// Save (extract) messages as new resources. Mirrors Angular ResourceService.SaveConversation
// (POST body = the selected messages' resourceIds) and SaveEntireConversation (POST to the
// anchor message's resource, no body). Fire-and-forget: the server queues the extract job.
export function saveSelectedMessages(projectId: string, collectionId: string, messages: ChatMessage[]) {
  return api.post(
    `/project/${projectId}/collection/${collectionId}/save`,
    messages.map((m) => m.resourceId),
  );
}

export function saveEntireConversation(projectId: string, collectionId: string, resourceId: string) {
  // No body argument: api.ts only omits the body for `undefined` — passing null would send
  // the literal string "null", where Angular's $http sent nothing at all.
  return api.post(`/project/${projectId}/collection/${collectionId}/resource/${resourceId}/save`);
}

/** Page fetch for the conversation LIST (left column) — GetConversations. */
export function fetchConversationsPage(
  projectId: string,
  collectionId: string,
  resourceId: string,
  page: number,
  limit: number = CONVERSATIONS_PAGE_SIZE,
): Promise<ConversationsResponse> {
  return api.get<ConversationsResponse>(
    `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/conversations?page=${page}&limit=${limit}`,
  );
}

/**
 * Imperative page fetch for the conversation message grid. ag-Grid's infinite row model
 * drives paging (it calls this from its datasource), so this is a plain function rather
 * than a hook — mirrors Angular's ResourceService.GetMessages datasource.
 *
 * `messageId` is the conversation anchor: the resource's own `chatMessageId` for the four
 * chat types, or the clicked conversation's most-recent message id for phone-image.
 */
export function fetchMessagesPage(
  projectId: string,
  collectionId: string,
  resourceId: string,
  messageId: string,
  page: number,
  limit: number = MESSAGES_PAGE_SIZE,
): Promise<MessagesResponse> {
  return api.get<MessagesResponse>(
    `/project/${projectId}/collection/${collectionId}/resource/${resourceId}` +
      `/messageId/${messageId}/messages?page=${page}&limit=${limit}`,
  );
}

/**
 * GetMessageIndex — the row index of the focused message within the paged message set, used
 * to scroll-to and briefly highlight the anchor message when conversation mode opens. The
 * endpoint returns a bare number, so fetch as text and coerce. Enabled only in conversation
 * mode with both ids present.
 */
export function useMessageFocusIndex(
  projectId: string,
  collectionId: string,
  resourceId: string,
  chatConversationId: string | null | undefined,
  chatMessageId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['message-focus-index', projectId, collectionId, resourceId, chatConversationId, chatMessageId],
    queryFn: async () => {
      const text = await api.getText(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}` +
          `/conversationId/${chatConversationId}/messageId/${chatMessageId}/index`,
      );
      const n = Number(text);
      return Number.isFinite(n) ? n : 0;
    },
    enabled: enabled && Boolean(projectId && collectionId && resourceId && chatConversationId && chatMessageId),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * GetConversationIndex — the row index of the anchor message's conversation within the LIST,
 * used to scroll the left column to it. Bare number; fetch as text and coerce.
 */
export function useConversationFocusIndex(
  projectId: string,
  collectionId: string,
  resourceId: string,
  chatMessageId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['conversation-focus-index', projectId, collectionId, resourceId, chatMessageId],
    queryFn: async () => {
      const text = await api.getText(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/messageId/${chatMessageId}/index`,
      );
      const n = Number(text);
      return Number.isFinite(n) ? n : 0;
    },
    enabled: enabled && Boolean(projectId && collectionId && resourceId && chatMessageId),
    staleTime: 5 * 60 * 1000,
  });
}
